import {
  getAuthModalSourceScreen,
  getTrackingScreen,
  SOURCE_SCREENS,
  type SourceScreen,
} from '@upzi/web-shell/source-screen';
import type { SearchMethod } from './index';

export {
  getAuthModalSourceScreen,
  getTrackingScreen,
  SOURCE_SCREENS,
  type SourceScreen,
};

const JOB_SEARCH_SOURCE_SCREEN_KEY = 'upzi_job_search_source_screen';
const JOB_SEARCH_METHOD_KEY = 'upzi_job_search_method';
const JOB_SEARCH_STARTED_AT_KEY = 'upzi_job_search_started_at';
const PROFILE_VIEW_SOURCE_SCREEN_KEY = 'upzi_profile_view_source_screen';
const JOB_DETAIL_PATH_PATTERN =
  /-(?:[1-9][0-9]{5,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export type OpportunityDetailSource = 'mobile_preview' | 'page' | 'preview';

type SourceScreenResolver = (
  pathname: string,
  searchParams?: URLSearchParams
) => string;

function getSessionStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function setSessionStorageItem(key: string, value: string): void {
  try {
    getSessionStorage()?.setItem(key, value);
  } catch {
    // Tracking must never block navigation when storage is unavailable.
  }
}

function consumeSessionStorageItem(key: string): string | undefined {
  const storage = getSessionStorage();

  if (!storage) return undefined;

  let value: string | null;

  try {
    value = storage.getItem(key);
  } catch {
    return undefined;
  }

  if (value === null) return undefined;

  try {
    storage.removeItem(key);
  } catch {
    // Keep the source usable even if the browser refuses cleanup.
  }

  return value;
}

function getReferrerSourceScreen(resolveSourceScreen: SourceScreenResolver) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return undefined;
  }

  if (!document.referrer) return undefined;

  try {
    const referrerUrl = new URL(document.referrer);

    if (referrerUrl.origin !== window.location.origin) {
      return undefined;
    }

    return resolveSourceScreen(referrerUrl.pathname, referrerUrl.searchParams);
  } catch {
    return undefined;
  }
}

function consumeSourceScreen(
  key: string,
  resolveSourceScreen: SourceScreenResolver
): string | undefined {
  return (
    consumeSessionStorageItem(key) ??
    getReferrerSourceScreen(resolveSourceScreen)
  );
}

export function getWebsiteTrackingScreen(
  pathname: string,
  searchParams?: URLSearchParams
): string {
  if (isJobPreviewScreen(pathname, searchParams)) {
    return SOURCE_SCREENS.JOB_PREVIEW;
  }

  switch (true) {
    case pathname.startsWith('/viec-lam/'):
      return JOB_DETAIL_PATH_PATTERN.test(pathname)
        ? SOURCE_SCREENS.JOB_DETAIL
        : SOURCE_SCREENS.SEARCH_RESULT;

    case pathname.startsWith('/cong-ty/') || pathname.startsWith('/company/'):
      return SOURCE_SCREENS.COMPANY_PROFILE;

    default:
      return getTrackingScreen(pathname);
  }
}

export function isJobPreviewScreen(
  pathname: string,
  searchParams?: URLSearchParams
): boolean {
  return Boolean(
    searchParams?.has('preview') &&
    (pathname === '/viec-lam' || pathname.startsWith('/viec-lam/'))
  );
}

export function getJobPreviewEntrySourceScreen(): SourceScreen {
  return SOURCE_SCREENS.SEARCH_RESULT;
}

export function getCurrentWebsiteTrackingScreen(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  return getWebsiteTrackingScreen(
    window.location.pathname,
    new URLSearchParams(window.location.search)
  );
}

export function rememberJobSearchSourceScreen(pathname: string): void {
  setSessionStorageItem(
    JOB_SEARCH_SOURCE_SCREEN_KEY,
    getTrackingScreen(pathname)
  );
}

export function rememberJobSearchMethod(method: SearchMethod): void {
  setSessionStorageItem(JOB_SEARCH_METHOD_KEY, method);
}

export function rememberJobSearchStartedAt(): void {
  setSessionStorageItem(JOB_SEARCH_STARTED_AT_KEY, String(Date.now()));
}

export function consumeJobSearchLoadingTime(): string {
  if (typeof window === 'undefined') return '0';

  const storedStartedAt = consumeSessionStorageItem(JOB_SEARCH_STARTED_AT_KEY);

  if (storedStartedAt) {
    const startedAt = Number(storedStartedAt);
    const loadingTimeMs = Date.now() - startedAt;

    if (Number.isFinite(startedAt) && loadingTimeMs >= 0) {
      return String(loadingTimeMs / 1000);
    }
  }

  return String(window.performance.now() / 1000);
}

export function consumeJobSearchMethod(): SearchMethod | undefined {
  if (typeof window === 'undefined') return undefined;

  const storedMethod = consumeSessionStorageItem(JOB_SEARCH_METHOD_KEY);

  if (
    storedMethod === 'type' ||
    storedMethod === 'suggestion_click' ||
    storedMethod === 'keyword_recommendation' ||
    storedMethod === 'history_click' ||
    storedMethod === 'cv_upload' ||
    storedMethod === 'via_deeplink'
  ) {
    return storedMethod;
  }

  return undefined;
}

export function rememberProfileViewSourceScreen(
  pathname: string,
  searchParams?: URLSearchParams
): void {
  setSessionStorageItem(
    PROFILE_VIEW_SOURCE_SCREEN_KEY,
    getWebsiteTrackingScreen(pathname, searchParams)
  );
}

export function consumeProfileViewSourceScreen(): string | undefined {
  return consumeSourceScreen(
    PROFILE_VIEW_SOURCE_SCREEN_KEY,
    getWebsiteTrackingScreen
  );
}

export function consumeJobSearchSourceScreen(): string | undefined {
  return consumeSourceScreen(JOB_SEARCH_SOURCE_SCREEN_KEY, getTrackingScreen);
}
